import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
  Button,
  ModalFooter,
  UncontrolledTooltip,
} from "reactstrap";
import styles from "../css/studyworkflow.module.scss";
import CommonTableIndexWise, {
  Column,
} from "Components/Common/CommonTableIndexWise";
import AddQueryModal from "./AddQueryModal";
import { showToastMessage } from "common/service/CommonService";
import { queryClient } from "api/queryClient";
import { formatDateTime, formatDateTimeIST } from "helpers/crf.utils";
import SimpleBar from "simplebar-react";
import CommonButton from "Components/Common/CommonButton";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "common/constants/PermissionConstants";
import { useQueryWorkflowData } from "modules/Library/Workflow/api/useWorkflowQuery";

interface QueryDetailsModalProps {
  isOpen: boolean;
  toggle: () => void;
  studyid?: number;
  stageid?: number;
  isRoleMatched?: boolean;
}

const QueryDetailsModal: React.FC<QueryDetailsModalProps> = ({
  isOpen,
  toggle,
  studyid,
  stageid,
  isRoleMatched,
}) => {
  const GroupSeparatorHandler: React.FC<{ isLatest: boolean }> = ({
    isLatest,
  }) => {
    const ref = React.useRef<HTMLSpanElement>(null);
    React.useLayoutEffect(() => {
      if (isLatest && ref.current) {
        const tr = ref.current.closest("tr");
        if (tr) {
          tr.classList.add(styles.queryGroupSeparator);
          // Also apply to all TDs to handle sticky columns and separate borders
          tr.querySelectorAll("td").forEach((td) => {
            td.classList.add(styles.queryGroupSeparator);
          });
        }
      }
    }, [isLatest]);
    return <span ref={ref} style={{ display: "none" }} />;
  };

  const [isAddQueryOpen, setIsAddQueryOpen] = useState(false);
  const [isRespondMode, setIsRespondMode] = useState(false);
  const [selectedQueryId, setSelectedQueryId] = useState<number | null>(null);

  const queryPayload = React.useMemo(
    () => ({
      studyid: studyid,
      pagenumber: 1,
      pagesize: 10,
    }),
    [studyid],
  );

  const { querySelectAll, updateQueryMutation } =
    useQueryWorkflowData(queryPayload);

  const toggleAddQuery = () => {
    if (isAddQueryOpen) {
      setIsRespondMode(false);
      setSelectedQueryId(null);
    }
    setIsAddQueryOpen(!isAddQueryOpen);
  };

  const handleAddQueryClick = () => {
    setIsRespondMode(false);
    setSelectedQueryId(null);
    setIsAddQueryOpen(true);
  };

  const queryData = React.useMemo(() => {
    const rawData = querySelectAll.data?.data || [];
    return rawData.flatMap((master: any, masterIndex: number) =>
      master.querydetails.map((detail: any, index: number) => ({
        index: rawData.length - masterIndex,
        queryid: master.querymasterid,
        status: detail.querystatus,
        event:
          detail.querystatus === "OPENED"
            ? "Created"
            : detail.querystatus === "RESPONDED"
              ? "Responded"
              : detail.querystatus === "CLOSED"
                ? "Closed"
                : detail.querystatus,
        by: detail.createdbyname,
        message: detail.querymessage,
        date: detail.createddate ? formatDateTime(detail.createddate) : "-",
        canclose: master.canclose,
        toreturnstage: master.toreturnstage,
        isMostRecent: index === 0,
        isVisualLast: index === master.querydetails.length - 1,
        isAbsoluteLast:
          masterIndex === rawData.length - 1 &&
          index === master.querydetails.length - 1,
      })),
    );
  }, [querySelectAll.data]);

  const handleAction = (type: string, row: any) => {
    if (type === "respond") {
      setSelectedQueryId(row.queryid);
      setIsRespondMode(true);
      setIsAddQueryOpen(true);
    } else if (type === "close") {
      const payload = {
        studyid: studyid || 11,
        querymasterid: row.queryid,
        currentstageid: stageid || 8,
        querymessage: "Query Close",
        querystatus: "CLOSED",
        updatereason: "Query Closed ",
      };

      updateQueryMutation.mutate(payload, {
        onSuccess: (response: any) => {
          queryClient.invalidateQueries({
            queryKey: ["workflow-query-selectall"],
          });
          showToastMessage(
            "success",
            response?.message || "Query closed successfully",
          );
        },
        onError: (error: any) => {
          showToastMessage("error", error?.message || "Failed to close query");
        },
      });
    }
  };

  const columns: Column[] = [
    // {
    //   key: "queryid",
    //   label: "Query ID",
    //   textCenter: true,
    //   sticky: true,
    //   render: (val: any, row: any) => (
    //     <>
    //       <GroupSeparatorHandler
    //         isLatest={row.isVisualLast && !row.isAbsoluteLast}
    //       />
    //       {val}
    //     </>
    //   ),
    // },
    
    { key: "index", label: "Query", sticky: true },
    { key: "event", label: "Event", sticky: true },
    {
      key: "message",
      label: "Query Message",
      sticky: true,
      render: (val: string, row: any, index?: number) => {
        const tooltipId = `query-msg-${row.queryid}-${index}`;
        const isLong = val && val.length > 10;
        return (
          <>
            <GroupSeparatorHandler
              isLatest={row.isVisualLast && !row.isAbsoluteLast}
            />
            <span
              id={tooltipId}
              className={`text-muted fw-medium d-inline-block ${isLong ? "text-truncate" : ""}`}
              style={{
                maxWidth: "70px",
                cursor: isLong ? "pointer" : "default",
              }}
            >
              {val}
            </span>
            {isLong && (
              <UncontrolledTooltip target={tooltipId} placement="bottom">
                {val}
              </UncontrolledTooltip>
            )}
          </>
        );
      },
    },
    { key: "by", label: "Event By", sticky: true },
    
    {
      key: "status",
      label: "Status",
      sticky: true,
      render: (val: string) => {
        const color =
          val === "OPENED"
            ? "primary"
            : val === "RESPONDED"
              ? "secondary"
              : "success";
        return (
          <Badge
            color={color}
            className={`bg-${color} text-white px-2 py-1 rounded-1 fw-medium`}
            style={{ fontSize: "11px" }}
          >
            {val}
          </Badge>
        );
      },
    },
    { key: "date", label: "Date & Time", sticky: true },
    {
      key: "action",
      label: "Action",
      sticky: true,
      render: (_: any, row: any) => {
        if (!row.isMostRecent || !isRoleMatched) return null;

        const isStageMatch =
          stageid &&
          row.toreturnstage &&
          Number(stageid) === Number(row.toreturnstage);

        if (row.status === "OPENED" && isStageMatch) {
          return (
            <CommonButton
              module={PERMISSION_MODULES.WORKFLOW_QUERY}
              action={PERMISSION_ACTIONS.RESPOND}
              color="primary"
              size="sm"
              className="fs-11 text-center fw-bold px-0"
              style={{ width: "65px" }}
              onClick={() => handleAction("respond", row)}
              disabled={!isRoleMatched}
            >
              Respond
            </CommonButton>
          );
        } else if (row.status === "RESPONDED" && row.canclose) {
          return (
            <CommonButton
              module={PERMISSION_MODULES.WORKFLOW_QUERY}
              action={PERMISSION_ACTIONS.CLOSE}
              color="success"
              size="sm"
              className="fs-11 text-center fw-bold px-0"
              style={{ width: "65px" }}
              onClick={() => handleAction("close", row)}
              disabled={!isRoleMatched}
            >
              Close
            </CommonButton>
          );
        }
        return null;
      },
    },
  ];

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        size="lg"
        backdrop="static"
        className="common_modal"
      >
        <ModalHeader toggle={toggle} className="p-3">
          <h5 className="fw-bold text-dark mb-0">Query Details</h5>
        </ModalHeader>
        <ModalBody className="px-2 py-1">
          {querySelectAll.isLoading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : (
            <SimpleBar style={{ maxHeight: "300px" }} className="pb-3">
              <CommonTableIndexWise
                columns={columns}
                data={queryData}
                paginationShow={false}
                pageSize={querySelectAll.isFetching ? 4 : 10}
                hideAction={true}
                loading={querySelectAll.isFetching}
              />
            </SimpleBar>
          )}
        </ModalBody>
        <ModalFooter className="border-top d-flex justify-content-end">
          {isRoleMatched &&
            querySelectAll.data?.data?.every(
              (q: any) => q.querystatus === "CLOSED",
            ) && (
              <CommonButton
                module={PERMISSION_MODULES.WORKFLOW_QUERY}
                action={PERMISSION_ACTIONS.ADD}
                type="button"
                color="danger"
                outline
                onClick={handleAddQueryClick}
                className="fw-bold px-4"
              >
                Add
              </CommonButton>
            )}
        </ModalFooter>
      </Modal>

      <AddQueryModal
        isOpen={isAddQueryOpen}
        toggle={toggleAddQuery}
        isRespond={isRespondMode}
        studyid={studyid}
        stageid={stageid}
        querymasterid={selectedQueryId || undefined}
      />
    </>
  );
};

export default QueryDetailsModal;
